// src/pages/cadastro/TelaCadastro.js — a tela do cadastro (out/26).
//
// Dos Claude Designs "Cadastro.dc.html" (Starter/Business) e "Cadastro Plus
// Black Friday.dc.html" (PLUS na campanha): os dois têm o MESMO miolo — topo
// com a marca, as 4 etapas, o cartão do formulário e um resumo ao lado — e
// mudam só o que vem em volta. Esta base é o miolo; cada visual entra com o
// próprio `topo`, `resumo` e as peças opcionais (escolha de plano na etapa 1,
// resumo do pedido na etapa 3, caixa da Meta na etapa 4).
//
// É SÓ apresentação: estado, validações, criação da conta, KYB, checkout do
// Stripe, retomada, contrato (o modal) e Embedded Signup continuam no
// Register.js e chegam por props — regra de cadastro nova entra lá, uma vez.
import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import EmbeddedSignupButton from '../../components/EmbeddedSignupButton';
import './cadastro.css';

export const ETAPAS = ['Identidade e Acesso', 'Dados Empresariais', 'Plano e Pagamento', 'Conectar WhatsApp'];

export const ICONE = {
  seta: 'M5 12h14 M12 5l7 7-7 7',
  check: 'M5 12.5l4.5 4.5L19 7.5',
  relogio: 'M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z M12 7.5v4.5l3 2',
  cadeado: 'M6 11h12v9H6z M8.5 11V8a3.5 3.5 0 0 1 7 0v3',
  escudo: 'M12 3l7.5 3v5.5c0 4.5-3.2 8.2-7.5 9.5c-4.3-1.3-7.5-5-7.5-9.5V6z M8.5 12.2l2.4 2.4l4.6-4.8',
  fechar: 'M6 6l12 12 M18 6L6 18',
};

const DOCUMENTOS = [
  { key: 'doc_cartao_cnpj', nome: 'Cartão CNPJ', icone: 'M7 3h7l4 4v14H7z M14 3v4h4 M10 12h5 M10 16h5' },
  { key: 'doc_contrato_social', nome: 'Contrato Social ou MEI', icone: 'M9 4h6v3H9z M7.5 5.5H5.5V21h13V5.5h-2 M9 12h6 M9 16h4' },
  { key: 'doc_comprovante_endereco', nome: 'Comprovante de Endereço', icone: 'M4 11l8-7 8 7 M6 9.5V20h12V9.5 M10 20v-5h4v5' },
];

export function Svg({ d, tamanho = 20, traco = 1.6, className }) {
  return (
    <svg className={className} width={tamanho} height={tamanho} viewBox="0 0 24 24" fill="none" stroke="currentColor"
      strokeWidth={traco} strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
      <path d={d} />
    </svg>
  );
}

// Um recurso do plano: texto, ou { texto, nota } quando precisa de uma
// segunda linha (o teto diário: "começa com 250/dia até o número aquecer").
// `como="span"` dentro do cartão de escolha, que é um <label> (lista não pode).
export function Recurso({ recurso, tamanho = 18, como: Tag = 'li' }) {
  const texto = typeof recurso === 'string' ? recurso : recurso.texto;
  const nota = typeof recurso === 'string' ? null : recurso.nota;
  return (
    <Tag className="cad-recurso">
      <Svg d={ICONE.check} tamanho={tamanho} traco={2} />
      <span>{texto}{nota && <span className="cad-recurso-nota">{nota}</span>}</span>
    </Tag>
  );
}

export const chaveRecurso = (r) => (typeof r === 'string' ? r : r.texto);

// "Só após 7 dias…" — ou, pra quem já teve assinatura, que a cobrança é no pagamento.
export function NotaCobranca({ testeDisponivel, className = 'cad-resumo-nota' }) {
  return (
    <div className={className}>
      <Svg d={ICONE.relogio} tamanho={18} traco={1.75} />
      {testeDisponivel
        ? <span><b>Só após 7 dias</b> a Cetto cobra a mensalidade. Cancele a qualquer momento.</span>
        : <span><b>Sem teste grátis</b>: a primeira mensalidade é cobrada no pagamento. Cancele a qualquer momento.</span>}
    </div>
  );
}

// A dica fica fora do <label>: é descrição do campo (aria-describedby =
// `${id}-dica`), não parte do nome que o leitor de tela anuncia.
function Campo({ id, rotulo, dica, largo, children }) {
  return (
    <div className={`cad-campo${largo ? ' cad-largo' : ''}`}>
      <label className="cad-campo-rot" htmlFor={id}>{rotulo}</label>
      {children}
      {dica && <span id={`${id}-dica`} className="cad-campo-dica" aria-live={id === 'cad-cnpj' ? 'polite' : undefined}>{dica}</span>}
    </div>
  );
}

function Principal({ carregando, children, ...resto }) {
  return (
    <button type="submit" className="cad-principal" disabled={carregando} aria-busy={carregando || undefined} {...resto}>
      {carregando && <span className="cad-gira" aria-hidden="true" />}
      {children}
    </button>
  );
}

function CabecalhoEtapa({ n, titulo, sub, nivel: Nivel }) {
  return (
    <>
      <p className="cad-etapa-num">Etapa {n} de {ETAPAS.length}</p>
      <Nivel className="cad-etapa-titulo">{titulo}</Nivel>
      <p className="cad-etapa-sub">{sub}</p>
    </>
  );
}

export default function TelaCadastro({
  // visual
  classe, tituloAba, topo, cabecalho, rotuloSecao, resumo,
  escolhaPlano = null, resumoPedido = null, caixaMeta = false, notaSeguranca = false, nivelEtapa = 'h2',
  // dados e handlers do Register.js
  plano, activeStep, setActiveStep, retomando, carregandoRetomada,
  error, setError, loading, cnpjLoading,
  session1, handleS1Change, session2, handleS2Change, setSession2,
  handleSubmitSession1, handleSubmitSession2, handleStartCheckout,
  handleAbrirContrato, testeDisponivel, codigoEnviadoPara, voltouComTeste,
  onEsqueciSenha, onEmbeddedSignupSuccess, onSkipWhatsApp,
}) {
  const secaoForm = useRef(null);
  const montou = useRef(false);

  useEffect(() => {
    const antes = document.title;
    document.title = tituloAba;
    return () => { document.title = antes; };
  }, [tituloAba]);

  // Trocou de etapa e o formulário não está à vista (clicou em Continuar no
  // fim de uma etapa longa, ou a página abriu direto no passo 2/3 da retomada
  // ou no 4 da volta do Stripe): leva até o começo dele.
  useEffect(() => {
    if (!montou.current) { montou.current = true; return; }
    const el = secaoForm.current;
    if (!el) return;
    const t = el.getBoundingClientRect().top;
    if (t < 0 || t > window.innerHeight * 0.6) {
      const reduzir = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      el.scrollIntoView({ behavior: reduzir ? 'auto' : 'smooth' });
    }
  }, [activeStep, carregandoRetomada]);

  const enviar = (fn) => (e) => { e.preventDefault(); if (!loading) fn(); };
  const preco = `${plano.price}${plano.priceUnit}`;

  const conectar = (
    <div className="cad-conectar">
      <EmbeddedSignupButton
        onSuccess={onEmbeddedSignupSuccess}
        onError={(err) => console.error('Embedded Signup error:', err)}
        deviceName={session1.company_name || undefined}
      />
    </div>
  );

  return (
    <div className={`cad ${classe}`}>
      <header className="cad-topo">
        <div className="cad-topo-in">
          <Link to="/" className="cad-marca" aria-label="Cetto System — início">
            <span className="cad-marca-nome"><span>C</span>ETTO</span>
            <span className="cad-marca-sub">SYSTEM</span>
          </Link>
          <Link to="/login" className="cad-entrar">Já tem conta?<span>Entrar →</span></Link>
        </div>
      </header>

      {topo}

      <section
        ref={secaoForm} className="cad-cadastro"
        aria-labelledby={cabecalho ? 'cad-cad-titulo' : undefined} aria-label={cabecalho ? undefined : rotuloSecao}
      >
        {cabecalho}

        <ol className="cad-etapas" aria-label="Etapas do cadastro">
          {ETAPAS.map((nome, i) => {
            const atual = i === activeStep;
            const feita = i < activeStep;
            // Só volta pra etapa já vista (não pula validação), e retomado o
            // passo 1 fica fechado — a conta já existe.
            const pode = i <= activeStep && !(retomando && i === 0) && !carregandoRetomada;
            return (
              <li key={nome}>
                <button
                  type="button" disabled={!pode || atual} aria-current={atual ? 'step' : undefined}
                  className={`cad-etapa${atual ? ' cad-atual' : ''}${feita ? ' cad-feita' : ''}`}
                  onClick={() => { if (pode) { setError(''); setActiveStep(i); } }}
                >
                  <span className="cad-etapa-n">{feita ? `✓ Etapa ${i + 1}` : `Etapa ${i + 1}`}</span>
                  <span className="cad-etapa-nome">{nome}</span>
                </button>
              </li>
            );
          })}
        </ol>

        <div className="cad-corpo">
          <div className="cad-cartao">
            {error && (
              <div className="cad-erro" role="alert">
                <span>
                  {error}
                  {/* Conta já existe e a pessoa não lembra a senha (out/26). */}
                  {error.startsWith('Este e-mail já tem conta') && (
                    <button type="button" className="cad-link" onClick={onEsqueciSenha}>Esqueci minha senha</button>
                  )}
                </span>
                <button type="button" className="cad-erro-fechar" aria-label="Fechar o aviso" onClick={() => setError('')}>
                  <Svg d={ICONE.fechar} tamanho={16} traco={2} />
                </button>
              </div>
            )}

            {carregandoRetomada && (
              <p className="cad-abrindo" role="status"><span className="cad-gira" aria-hidden="true" />Abrindo o seu cadastro…</p>
            )}

            {/* ── Etapa 1 — Identidade e Acesso ── */}
            {activeStep === 0 && !carregandoRetomada && (
              <form onSubmit={enviar(handleSubmitSession1)} noValidate>
                <CabecalhoEtapa n={1} nivel={nivelEtapa} titulo="Identidade e Acesso" sub="Seus dados de acesso à plataforma Cetto System" />
                {escolhaPlano}
                <div className={`cad-grade${escolhaPlano ? ' cad-grade-separada' : ''}`}>
                  <Campo id="cad-nome" rotulo="Nome completo">
                    <input className="cad-input" type="text" id="cad-nome" name="name" autoComplete="name" value={session1.name}
                      onChange={handleS1Change} placeholder="Digite seu nome completo" />
                  </Campo>
                  <Campo id="cad-empresa" rotulo="Nome da empresa">
                    <input className="cad-input" type="text" id="cad-empresa" name="company_name" autoComplete="organization" value={session1.company_name}
                      onChange={handleS1Change} placeholder="Digite o nome da sua empresa" />
                  </Campo>
                  <Campo id="cad-email" rotulo="E-mail profissional" largo>
                    <input className="cad-input" type="email" id="cad-email" name="email" autoComplete="email" value={session1.email}
                      onChange={handleS1Change} placeholder="Digite seu e-mail principal" />
                  </Campo>
                  <Campo id="cad-senha" rotulo="Senha" dica="Mínimo 8 caracteres">
                    <input className="cad-input" type="password" id="cad-senha" aria-describedby="cad-senha-dica" name="password" autoComplete="new-password" value={session1.password}
                      onChange={handleS1Change} placeholder="•••••••••••" />
                  </Campo>
                  <Campo id="cad-confirmar" rotulo="Confirmar senha">
                    <input className="cad-input" type="password" id="cad-confirmar" name="confirmPassword" autoComplete="new-password" value={session1.confirmPassword}
                      onChange={handleS1Change} placeholder="•••••••••••" />
                  </Campo>
                </div>
                <Principal carregando={loading} style={{ marginTop: 32, width: '100%' }}>
                  Continuar <Svg d={ICONE.seta} tamanho={18} traco={1.75} />
                </Principal>
                {notaSeguranca && (
                  <p className="cad-seguro"><Svg d={ICONE.cadeado} tamanho={14} traco={1.8} />Conexão protegida. Seus dados seguem a LGPD.</p>
                )}
              </form>
            )}

            {/* ── Etapa 2 — Dados Empresariais ── */}
            {activeStep === 1 && (
              <form onSubmit={enviar(handleSubmitSession2)} noValidate>
                <CabecalhoEtapa n={2} nivel={nivelEtapa} titulo="Dados Empresariais" sub="Informações da sua empresa para a API Oficial Meta" />

                {codigoEnviadoPara && (
                  <div className="cad-nota cad-nota-azul" role="note">
                    <Svg d="M4 6h16v12H4z M4 7l8 6 8-6" />
                    <span>Enviamos um código para <b>{codigoEnviadoPara}</b> para confirmar seu e-mail. Não precisa ser agora: você confirma pelo painel quando quiser.</span>
                  </div>
                )}

                <div className="cad-grade">
                  <Campo id="cad-cnpj" rotulo="CNPJ" dica={cnpjLoading ? 'Buscando a Razão Social…' : 'Digite o CNPJ para preencher automaticamente a Razão Social'}>
                    <span className="cad-input-caixa">
                      <input className="cad-input cad-num" type="text" id="cad-cnpj" aria-describedby="cad-cnpj-dica" name="cnpj" inputMode="numeric" value={session2.cnpj}
                        onChange={handleS2Change} placeholder="00.000.000/0000-00" />
                      {cnpjLoading && <span className="cad-gira cad-gira-campo" aria-hidden="true" />}
                    </span>
                  </Campo>
                  <Campo id="cad-razao" rotulo="Razão Social" dica="Pode editar se necessário">
                    <input className="cad-input" type="text" id="cad-razao" aria-describedby="cad-razao-dica" name="razao_social" autoComplete="organization" value={session2.razao_social}
                      onChange={handleS2Change} placeholder="Preenchido automaticamente" />
                  </Campo>
                  <Campo id="cad-site" rotulo={<>Website <span className="cad-fraco">— exigido pela Meta para o perfil WhatsApp Business</span></>} largo>
                    <input className="cad-input" type="url" id="cad-site" name="website" autoComplete="url" value={session2.website}
                      onChange={handleS2Change} placeholder="https://suaempresa.com.br" />
                  </Campo>
                </div>

                <div className="cad-caixa cad-caixa-lilas">
                  <Svg d="M13 3L5 13h6l-1 8 8-10h-6z" tamanho={22} />
                  <div>
                    <p className="cad-caixa-tit">Cadastro simplificado — sem campos técnicos da Meta</p>
                    <p><b>BM ID</b> é capturado automaticamente quando você conectar o WhatsApp (Etapa 4).<br />
                      <b>Nome de exibição</b>, <b>categoria</b> e <b>descrição</b> ficam pra depois — você configura em <span className="cad-lilas">Gerenciamento → Meta</span>.</p>
                  </div>
                </div>

                <div className="cad-bloco">
                  <p className="cad-bloco-rot">Documentos da empresa <span className="cad-fraco">— opcional, para verificação KYB</span></p>
                  <div className="cad-docs">
                    {DOCUMENTOS.map((doc) => {
                      const arquivo = session2[doc.key];
                      return (
                        <label key={doc.key} className={`cad-doc${arquivo ? ' cad-doc-ok' : ''}`}>
                          <Svg d={doc.icone} tamanho={22} />
                          <span className="cad-doc-nome">{doc.nome}</span>
                          <span className="cad-doc-status">{arquivo ? `✓ ${arquivo.name}` : 'Clique para enviar'}</span>
                          <input type="file" className="cad-oculto" accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => { const f = e.target.files && e.target.files[0]; if (f) setSession2((p) => ({ ...p, [doc.key]: f })); }} />
                        </label>
                      );
                    })}
                  </div>
                  <p className="cad-campo-dica">PDF ou imagem. Máximo 5MB. Usados para aprovação junto à Meta.</p>
                </div>

                <div className="cad-bloco">
                  <p className="cad-bloco-rot">Contrato de prestação de serviços</p>
                  <div className={`cad-termos${session2.terms_accepted ? ' cad-termos-ok' : ''}`}>
                    {/* Marcar abre o contrato (o aceite só vem depois de ler até o
                        fim, no modal); desmarcar desfaz o aceite. */}
                    <button
                      type="button" role="checkbox" aria-checked={session2.terms_accepted} className="cad-caixinha"
                      aria-label="Aceitar o Contrato de Prestação de Serviços Cetto System"
                      onClick={() => {
                        if (session2.terms_accepted) setSession2((p) => ({ ...p, terms_accepted: false }));
                        else handleAbrirContrato();
                      }}
                    >
                      <Svg d={ICONE.check} tamanho={14} traco={3} />
                    </button>
                    <p>Li e aceito o{' '}
                      <button type="button" className="cad-link" onClick={handleAbrirContrato}>Contrato de Prestação de Serviços Cetto System</button>
                      {' '}e os Termos de Uso da Meta Business API.</p>
                  </div>
                </div>

                <div className="cad-acoes">
                  {!retomando && <button type="button" className="cad-voltar" onClick={() => { setError(''); setActiveStep(0); }}>← Voltar</button>}
                  <Principal carregando={loading}>Continuar para Pagamento <Svg d={ICONE.seta} tamanho={18} traco={1.75} /></Principal>
                </div>
              </form>
            )}

            {/* ── Etapa 3 — Plano e Pagamento ── */}
            {activeStep === 2 && (
              <form onSubmit={enviar(handleStartCheckout)} noValidate>
                <CabecalhoEtapa n={3} nivel={nivelEtapa} titulo="Plano e Pagamento" sub="Confira o plano e configure o pagamento recorrente" />

                {resumoPedido}

                {testeDisponivel ? (
                  <div className={`cad-teste${resumoPedido ? ' cad-teste-colado' : ''}`}>
                    <p className="cad-teste-tit">7 dias grátis pra você testar tudo</p>
                    <div className="cad-linha-tempo">
                      <span className="cad-ponto cad-ponto-hoje" aria-hidden="true" />
                      <span className="cad-lt-quando">Hoje</span>
                      <span className="cad-lt-valor cad-verde">R$ 0,00</span>
                      <span className="cad-lt-fio" aria-hidden="true" /><span /><span />
                      <span className="cad-ponto" aria-hidden="true" />
                      <span className="cad-lt-quando cad-lt-depois">A partir do 8º dia</span>
                      <span className="cad-lt-valor">{preco}</span>
                    </div>
                    <p className="cad-teste-txt">Você <b>não é cobrado hoje</b>. A cobrança acontece só no <b>8º dia</b> — e você pode cancelar antes disso pelo painel, sem qualquer débito. O cartão fica salvo com segurança na Stripe pra assinar automaticamente se você gostar.</p>
                  </div>
                ) : (
                  <div className={`cad-caixa cad-caixa-ambar${resumoPedido ? ' cad-teste-colado' : ''}`}>
                    <Svg d={ICONE.relogio} />
                    <div>
                      <p className="cad-caixa-tit">Sua empresa já usou os 7 dias grátis</p>
                      <p>O teste vale uma vez por empresa. Ao confirmar no Stripe, a primeira mensalidade de <b>{plano.price}</b> é cobrada hoje e você pode cancelar quando quiser pelo painel.</p>
                    </div>
                  </div>
                )}

                <div className="cad-caixa">
                  <Svg d={ICONE.cadeado} className="cad-verde" />
                  <div>
                    <p className="cad-caixa-tit">Pagamento 100% seguro via Stripe</p>
                    <p>Cartão processado direto pela Stripe — Cetto não armazena. Cancele quando quiser pelo painel.</p>
                  </div>
                </div>

                <div className="cad-acoes">
                  <button type="button" className="cad-voltar" onClick={() => { setError(''); setActiveStep(1); }}>← Voltar</button>
                  <Principal carregando={loading}>
                    {loading ? 'Redirecionando…' : testeDisponivel ? 'Começar 7 dias grátis' : 'Ir para o pagamento'}
                  </Principal>
                </div>
              </form>
            )}

            {/* ── Etapa 4 — Conectar WhatsApp ── */}
            {activeStep === 3 && (
              <div>
                <div className="cad-feito">
                  <span className="cad-feito-icone"><Svg d={ICONE.check} tamanho={28} traco={2} /></span>
                  <p className="cad-etapa-num">Etapa 4 de {ETAPAS.length}</p>
                  <CabecalhoFeito nivel={nivelEtapa}>
                    {voltouComTeste === true ? 'Teste de 7 dias ativado!'
                      : voltouComTeste === false ? 'Pagamento confirmado!'
                      : 'Assinatura confirmada!'}
                  </CabecalhoFeito>
                  <p className="cad-feito-txt">
                    {voltouComTeste === true && 'Nada foi cobrado hoje: a primeira mensalidade fica para o 8º dia. '}
                    Última etapa: conecte seu WhatsApp Business para começar a operar. O fluxo é 100% oficial Meta — sem campos técnicos pra preencher.
                  </p>
                </div>

                <div className="cad-cenarios">
                  <div className="cad-cenario">
                    <span className="cad-selo cad-selo-azul">Cenário 1</span>
                    <p className="cad-caixa-tit">Sem conta na Meta</p>
                    <p>A Meta cria um Business Manager automaticamente. Você só faz login com Facebook.</p>
                  </div>
                  <div className="cad-cenario">
                    <span className="cad-selo cad-selo-lilas">Cenário 2</span>
                    <p className="cad-caixa-tit">Já tem Meta Business</p>
                    <p>A Meta detecta seu BM existente e lista seus números. Você seleciona qual usar.</p>
                  </div>
                </div>

                {caixaMeta ? (
                  <div className="cad-caixa-meta">
                    <div className="cad-caixa-meta-txt">
                      <Svg d={ICONE.escudo} tamanho={22} />
                      <div>
                        <p className="cad-caixa-tit">Conexão feita na janela da própria Meta</p>
                        <p>Você faz login direto com o Facebook. A Cetto não vê nem guarda sua senha.</p>
                      </div>
                    </div>
                    {conectar}
                  </div>
                ) : conectar}

                <div className="cad-avisos">
                  <div className="cad-nota cad-nota-ambar">
                    <Svg d="M12 4l9 16H3z M12 10v4 M12 17v0.5" />
                    <span><b>Atenção:</b> ao registrar um número, ele será desvinculado do WhatsApp pessoal ou Business App. Recomendamos um número exclusivo para a empresa.</span>
                  </div>
                  <div className="cad-nota cad-nota-azul">
                    <Svg d="M12 3a9 9 0 1 0 0 18a9 9 0 0 0 0-18z M12 11v5 M12 8v0.5" />
                    <span><b>Número em outra plataforma?</b> Se está em Wati, BotConversa ou outra API, fale com o suporte pra migrar gratuitamente.</span>
                  </div>
                </div>

                <div className="cad-depois">
                  <p>Prefere conectar depois?</p>
                  <button type="button" className="cad-depois-botao" onClick={onSkipWhatsApp}>Conectar WhatsApp depois — ir pro dashboard →</button>
                  <p className="cad-campo-dica">Você poderá conectar a qualquer momento pelo botão "Conectar WhatsApp" no menu superior do dashboard.</p>
                </div>
              </div>
            )}
          </div>

          {resumo}
        </div>
      </section>
    </div>
  );
}

function CabecalhoFeito({ nivel: Nivel, children }) {
  return <Nivel className="cad-etapa-titulo">{children}</Nivel>;
}
